type RecordType = "CNAME" | "TXT" | "NS";

interface BaseRecord {
  host: string;
  ttl: number;
  type: RecordType;
  class?: string;
}

interface CNAMERecord extends BaseRecord {
  type: "CNAME";
  value: string;
}

interface TXTRecord extends BaseRecord {
  type: "TXT";
  value: string | string[];
}

interface NSRecord extends BaseRecord {
  type: "NS";
  value: string;
}

type DNSRecord = CNAMERecord | TXTRecord | NSRecord;

interface ZoneFileOptions {
  sortRecords?: boolean;
  wrapTxtRecords?: boolean;
}

/**
 * Exports DNS records to zone file format
 * @param records Array of DNS records
 * @param options Configuration options for zone file generation
 * @returns String containing the zone file content
 */
export function exportZoneFile(records: DNSRecord[], options: ZoneFileOptions = {}): string {
  const { sortRecords = true, wrapTxtRecords = true } = options;

  const lines: string[] = [];

  // Sort records if requested
  let processedRecords = [...records];
  if (sortRecords) {
    processedRecords = sortRecordsByType(processedRecords);
  }

  // Process each record
  for (const record of processedRecords) {
    const line = formatRecord(record, wrapTxtRecords);
    lines.push(line);
  }

  return lines.join("\n");
}

/**
 * Formats a single DNS record to zone file format
 */
function formatRecord(record: DNSRecord, wrapTxtRecords: boolean): string {
  const host = formatHost(record.host);
  const ttl = record.ttl;
  const recordClass = record.class || "IN";

  const baseFormat = `${host}\t${ttl}\t${recordClass}\t${record.type}`;

  switch (record.type) {
    case "NS":
      return `${baseFormat}\t${formatDomain(record.value)}`;

    case "CNAME":
      return `${baseFormat}\t${formatDomain(record.value)}`;

    case "TXT":
      return `${baseFormat}\t${formatTxtValue(record.value, wrapTxtRecords)}`;

    default:
      throw new Error(`Unsupported record type: ${(record as any).type}`);
  }
}

/**
 * Formats the name field (handles @ and empty names)
 */
function formatHost(host: string): string {
  if (!host || host === "@" || host === "") {
    return "@";
  }
  // Ensure proper formatting (no trailing dots for relative hosts)
  return host.endsWith(".") ? host : host;
}

/**
 * Formats domain values (ensures trailing dot for FQDNs)
 */
function formatDomain(domain: string): string {
  if (!domain) return ".";
  if (domain === "@") return "@";
  // Add trailing dot if it looks like a FQDN and doesn't have one
  if (domain.includes(".") && !domain.endsWith(".")) {
    return `${domain}.`;
  }
  return domain;
}

/**
 * Formats TXT record values (handles strings and arrays)
 */
function formatTxtValue(value: string | string[], wrap: boolean): string {
  const values = Array.isArray(value) ? value : [value];

  return values
    .map((v) => {
      // Escape quotes in the value
      const escaped = v.replace(/"/g, '\\"');

      // Wrap long TXT records if requested (255 char limit per string)
      if (wrap && escaped.length > 255) {
        const chunks: string[] = [];
        for (let i = 0; i < escaped.length; i += 255) {
          chunks.push(escaped.substring(i, i + 255));
        }
        return chunks.map((chunk) => `"${chunk}"`).join(" ");
      }

      return `"${escaped}"`;
    })
    .join(" ");
}

/**
 * Formats SOA records (special multi-line format)
 */
function formatSOARecord(name: string, ttl: number, recordClass: string, record: SOARecord): string {
  return [
    `${name}\t${ttl}\t${recordClass}\tSOA\t${formatDomain(record.mname)} ${formatDomain(record.rname)} (`,
    `\t\t\t\t${record.serial}\t; Serial`,
    `\t\t\t\t${record.refresh}\t; Refresh`,
    `\t\t\t\t${record.retry}\t; Retry`,
    `\t\t\t\t${record.expire}\t; Expire`,
    `\t\t\t\t${record.minimum} )\t; Minimum TTL`,
  ].join("\n");
}

/**
 * Sorts records by type for better organization
 */
function sortRecordsByType(records: DNSRecord[]): DNSRecord[] {
  const typeOrder: RecordType[] = ["NS", "CNAME", "TXT"];

  return records.sort((a, b) => {
    const aIndex = typeOrder.indexOf(a.type);
    const bIndex = typeOrder.indexOf(b.type);

    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }

    // Secondary sort by host
    return (a.host || "@").localeCompare(b.host || "@");
  });
}

/**
 * Validates DNS records before export
 */
export function validateRecords(records: DNSRecord[]): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  for (let i = 0; i < records.length; i++) {
    const record = records[i];

    // Check for required fields
    if (!record.type) {
      errors.push(`Record ${i}: Missing record type`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// Example usage:
const exampleRecords: DNSRecord[] = [
  {
    type: "NS",
    host: "@",
    value: "ns1.example.com",
    ttl: 600,
  },
  {
    type: "NS",
    host: "@",
    value: "ns2.example.com",
    ttl: 600,
  },
  {
    type: "CNAME",
    host: "blog",
    value: "www.example.com",
    ttl: 600,
  },
  {
    type: "TXT",
    host: "@",
    value: "v=spf1 include:_spf.google.com ~all",
    ttl: 600,
  },
  {
    type: "TXT",
    host: "_dmarc",
    value: "v=DMARC1; p=quarantine; rua=mailto:dmarc@example.com",
    ttl: 600,
  },
];

// Generate zone file
// const zoneFile = exportZoneFile(exampleRecords, {
//   origin: "example.com.",
//   defaultTTL: 3600,
//   includeHeader: true,
//   sortRecords: true,
// });

// console.log(zoneFile);
